import {
  createFrontendModule,
  createRouteRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { jsx } from 'react/jsx-runtime';
import CategoryIcon from '@material-ui/icons/Category';
import { catalogTableColumnsFunc } from './catalog-table-columns-func';

// Overrides plugin-catalog's own page:catalog extension - a no-name
// makeWithOverrides() registered under pluginId 'catalog' produces the same
// id `page:catalog` the stock page registers under, so this replaces it.
//
// It renders our CatalogIndexPage (modules/catalog-index-page): the stock
// EntityListProvider, pickers and CatalogTable in the Paradigm layout. The
// page places its own filters, so the stock `filters` input is not declared
// and the eight catalog-filter:catalog/* extensions are disabled in
// app-config.yaml.
//
// `exportConfig` (the stock extension's other input) is deliberately not
// re-declared - its data ref isn't exported publicly, and nothing in this
// app uses CatalogExportConfigBlueprint, so there's nothing that would ever
// attach to it.
//
// The stock extension also passes a `routeRef` (its own module-private
// `rootRouteRef`, id "catalog") - not exported by @backstage/plugin-catalog
// under either the "." or "./alpha" entrypoints, so it can't be imported
// here without a fragile deep import into the package's dist internals.
// AppNav's sidebar renderer (@backstage/plugin-app's AppNav.esm.js) drops a
// page node from `navItems` entirely when it has no routeRef - this runs
// BEFORE the title/icon check below, so a page missing routeRef never shows
// in the sidebar regardless of title/icon. Fixed by minting our own
// routeRef here - nothing in this app declares an external-route-ref
// binding targeting the catalog plugin's root route, so there's no other
// consumer that needs the exact stock instance.
const catalogRootRouteRef = createRouteRef();
const catalogPage = PageBlueprint.makeWithOverrides({
  factory(originalFactory) {
    return originalFactory({
      path: '/catalog',
      routeRef: catalogRootRouteRef,
      // The stock factory sets these too (title: "Catalog", the same
      // CategoryIcon). PageBlueprint only emits core.title/core.icon when
      // both are supplied, and without them the Catalog link disappears from
      // the sidebar while the page stays reachable by URL.
      icon: jsx(CategoryIcon, { fontSize: 'inherit' }),
      title: 'Catalog',
      loader: async () => {
        const { CatalogIndexPage } = await import(
          '../catalog-index-page/CatalogIndexPage'
        );
        return jsx(CatalogIndexPage, {
          columns: catalogTableColumnsFunc,
          // OFFSET PAGINATION: 20 rows per page with page numbers, instead of
          // the stock cursor mode. The footer's "Page X of Y" is
          // catalog-index-page/table/CatalogTableCard.tsx.
          pagination: { mode: 'offset', limit: 20 },
          // The page renders its own toolbar (search, filters, count).
          tableOptions: {
            toolbar: false,
            pageSizeOptions: [20, 50, 100],
            // The mock has prev/next only.
            showFirstLastPageButtons: false,
          },
        });
      },
    });
  },
});

export const catalogColumnsModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [catalogPage],
});
